import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { showCustomToast } from '../common/CustomToast';
import './WithdrawalModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface WithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type WithdrawalView = 'methods' | 'solana';

const WithdrawalModal: React.FC<WithdrawalModalProps> = ({ isOpen, onClose }) => {
  const [currentView, setCurrentView] = useState<WithdrawalView>('methods');
  const [isClosing, setIsClosing] = useState(false);
  const [solanaAddress, setSolanaAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, token } = useSelector((state: RootState) => state.auth);

  if (!isOpen || !user) return null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      // Reset view and form when closing
      setTimeout(() => {
        setCurrentView('methods');
        setSolanaAddress('');
        setAmount('');
      }, 100);
    }, 300);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleSubmitWithdrawal = async () => {
    if (!solanaAddress || !amount) {
      showCustomToast.error('Please enter both address and amount');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      showCustomToast.error('Please enter a valid amount');
      return;
    }

    const userBalanceSOL = Number(user.balance) / 1_000_000_000;
    if (amountNum > userBalanceSOL) {
      showCustomToast.error('Insufficient balance');
      return;
    }

    if (amountNum < 0.01) {
      showCustomToast.error('Minimum withdrawal is 0.01 SOL');
      return;
    }

    // Validate Solana address format
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
    if (!base58Regex.test(solanaAddress)) {
      showCustomToast.error('Invalid Solana address format');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/withdrawals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: amountNum,
          solanaAddress
        })
      });

      const data = await response.json();

      if (response.ok) {
        showCustomToast.success('Withdrawal request submitted! Awaiting admin approval.');
        handleClose();
      } else {
        // Show specific error message from backend
        showCustomToast.error(data.error || 'Withdrawal request failed');
      }
    } catch (error) {
      console.error('Withdrawal error:', error);
      showCustomToast.error('Failed to connect to server. Please check if the backend is running.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const userBalanceSOL = Number(user.balance) / 1_000_000_000;

  return (
    <div className={`withdrawal-modal-backdrop ${isClosing ? 'closing' : ''}`} onClick={handleBackdropClick}>
      <div className={`withdrawal-modal ${isClosing ? 'closing' : ''}`}>
        {currentView === 'methods' ? (
          <>
            <div className="withdrawal-modal-header">
              <h2>Withdraw</h2>
              <button className="close-btn" onClick={handleClose}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="withdrawal-modal-body">
              <h3 className="method-title">Crypto Methods</h3>
              
              <button 
                className="crypto-method-btn"
                onClick={() => setCurrentView('solana')}
              >
                <div className="crypto-method-icon">
                  <svg width="24" height="24" viewBox="0 0 397.7 311.7" fill="currentColor">
                    <defs>
                      <linearGradient id="solanaGradientWithdraw" x1="360.8791" y1="351.4553" x2="141.213" y2="-69.2936" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stopColor="#00FFA3"/>
                        <stop offset="1" stopColor="#DC1FFF"/>
                      </linearGradient>
                      <linearGradient id="solanaGradient2Withdraw" x1="264.8291" y1="401.6014" x2="45.163" y2="-19.1475" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stopColor="#00FFA3"/>
                        <stop offset="1" stopColor="#DC1FFF"/>
                      </linearGradient>
                      <linearGradient id="solanaGradient3Withdraw" x1="312.5484" y1="376.688" x2="92.8822" y2="-44.061" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stopColor="#00FFA3"/>
                        <stop offset="1" stopColor="#DC1FFF"/>
                      </linearGradient>
                    </defs>
                    <path d="M64.6,237.9c2.4-2.4,5.7-3.8,9.2-3.8h317.4c5.8,0,8.7,7,4.6,11.1l-62.7,62.7c-2.4,2.4-5.7,3.8-9.2,3.8H6.5c-5.8,0-8.7-7-4.6-11.1L64.6,237.9z" fill="url(#solanaGradientWithdraw)"/>
                    <path d="M64.6,3.8C67.1,1.4,70.4,0,73.8,0h317.4c5.8,0,8.7,7,4.6,11.1l-62.7,62.7c-2.4,2.4-5.7,3.8-9.2,3.8H6.5c-5.8,0-8.7-7-4.6-11.1L64.6,3.8z" fill="url(#solanaGradient2Withdraw)"/>
                    <path d="M333.1,120.1c-2.4-2.4-5.7-3.8-9.2-3.8H6.5c-5.8,0-8.7,7-4.6,11.1l62.7,62.7c2.4,2.4,5.7,3.8,9.2,3.8h317.4c5.8,0,8.7-7,4.6-11.1L333.1,120.1z" fill="url(#solanaGradient3Withdraw)"/>
                  </svg>
                </div>
                <div className="crypto-method-info">
                  <span className="crypto-name">Solana</span>
                  <span className="crypto-symbol">SOL</span>
                </div>
                <svg className="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="withdrawal-modal-header">
              <button className="back-btn" onClick={() => setCurrentView('methods')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                Back to methods
              </button>
              <button className="close-btn" onClick={handleClose}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="withdrawal-modal-body">
              <h3 className="network-title">Solana Network</h3>
              <p className="network-description">
                Enter your Solana address and the amount you wish to withdraw. Your request will be reviewed by our admins.
              </p>

              <div className="balance-info">
                <span className="balance-label">Available Balance:</span>
                <span className="balance-value">{userBalanceSOL.toFixed(4)} SOL</span>
              </div>

              <div className="address-section">
                <label className="address-label">Your SOL Address</label>
                <div className="address-input-wrapper">
                  <div className="address-icon">
                    <svg width="18" height="18" viewBox="0 0 397.7 311.7" fill="currentColor">
                      <defs>
                        <linearGradient id="solIconGrad1" x1="360.8791" y1="351.4553" x2="141.213" y2="-69.2936" gradientUnits="userSpaceOnUse">
                          <stop offset="0" stopColor="#00FFA3"/>
                          <stop offset="1" stopColor="#DC1FFF"/>
                        </linearGradient>
                        <linearGradient id="solIconGrad2" x1="264.8291" y1="401.6014" x2="45.163" y2="-19.1475" gradientUnits="userSpaceOnUse">
                          <stop offset="0" stopColor="#00FFA3"/>
                          <stop offset="1" stopColor="#DC1FFF"/>
                        </linearGradient>
                        <linearGradient id="solIconGrad3" x1="312.5484" y1="376.688" x2="92.8822" y2="-44.061" gradientUnits="userSpaceOnUse">
                          <stop offset="0" stopColor="#00FFA3"/>
                          <stop offset="1" stopColor="#DC1FFF"/>
                        </linearGradient>
                      </defs>
                      <path d="M64.6,237.9c2.4-2.4,5.7-3.8,9.2-3.8h317.4c5.8,0,8.7,7,4.6,11.1l-62.7,62.7c-2.4,2.4-5.7,3.8-9.2,3.8H6.5c-5.8,0-8.7-7-4.6-11.1L64.6,237.9z" fill="url(#solIconGrad1)"/>
                      <path d="M64.6,3.8C67.1,1.4,70.4,0,73.8,0h317.4c5.8,0,8.7,7,4.6,11.1l-62.7,62.7c-2.4,2.4-5.7,3.8-9.2,3.8H6.5c-5.8,0-8.7-7-4.6-11.1L64.6,3.8z" fill="url(#solIconGrad2)"/>
                      <path d="M333.1,120.1c-2.4-2.4-5.7-3.8-9.2-3.8H6.5c-5.8,0-8.7,7-4.6,11.1l62.7,62.7c2.4,2.4,5.7,3.8,9.2,3.8h317.4c5.8,0,8.7-7,4.6-11.1L333.1,120.1z" fill="url(#solIconGrad3)"/>
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={solanaAddress}
                    onChange={(e) => setSolanaAddress(e.target.value)}
                    placeholder="Enter your Solana wallet address"
                    className="address-input"
                  />
                </div>
              </div>

              <div className="amount-section">
                <label className="amount-label">Amount (SOL)</label>
                <div className="amount-input-wrapper">
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0.01"
                    max={userBalanceSOL}
                    className="amount-input"
                  />
                  <button 
                    className="max-btn"
                    onClick={() => setAmount(userBalanceSOL.toString())}
                  >
                    MAX
                  </button>
                </div>
                <div className="amount-hint">Minimum: 0.01 SOL</div>
              </div>

              <button 
                className="send-btn"
                onClick={handleSubmitWithdrawal}
                disabled={isSubmitting || !solanaAddress || !amount}
              >
                {isSubmitting ? 'Submitting...' : 'Send Request'}
              </button>

              <div className="withdrawal-note">
                <p><strong>Note:</strong> Withdrawals are manually reviewed by our team. You will receive a notification once your request is processed.</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WithdrawalModal;
